/* =============================================================
   useProcessTemplates.js — CRUD for university_process_templates
   -------------------------------------------------------------
   Manages the list of process templates for a university.
   Each template defines a named process (e.g. "Master Incoming")
   that can be auto-matched to students or manually assigned.

   Returns:
     templates       — all templates for this university, both types
     loading / error / saving
     createTemplate  — creates a new empty template
     updateTemplate  — renames or changes match rules
     deleteTemplate  — deletes template + its steps (cascade)
     setDefault      — marks one template as default for its type
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';

export function useProcessTemplates() {
  const { user } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);


  /* ── fetch ─────────────────────────────────────────────────── */
  const fetch = useCallback(async () => {
    if (!user?.universityId) return;
    setLoading(true);
    const { data, error: e } = await supabase
      .from('university_process_templates')
      .select('id, name, type, semester_id, match_degree, match_categories, is_default, created_at')
      .eq('university_id', user.universityId)
      .order('type')
      .order('created_at');
    if (e) setError(e.message);
    else   setTemplates(data ?? []);
    setLoading(false);
  }, [user?.universityId]);

  useEffect(() => { fetch(); }, [fetch]);


  /* ── createTemplate ─────────────────────────────────────────── */
  const createTemplate = async ({ name, type, semesterId = null, copyFromId = null, matchDegree = [], matchCategories = [], isDefault = false }) => {
    if (!user?.universityId || !name?.trim()) return null;
    setSaving(true);
    setError(null);

    // 1. Create the template
    const { data, error: e } = await supabase
      .from('university_process_templates')
      .insert({
        university_id:    user.universityId,
        name:             name.trim(),
        type,
        semester_id:      semesterId || null,
        match_degree:     matchDegree,
        match_categories: matchCategories,
        is_default:       isDefault,
      })
      .select('id')
      .single();
    if (e) { setError(e.message); setSaving(false); return null; }

    const newId = data.id;

    // 2. If copyFromId provided, copy steps + documents from source
    if (copyFromId) {
      const { data: sourceSteps, error: fetchErr } = await supabase
        .from('process_steps')
        .select('label, step_order, process_step_documents(document_type_id, item_type, deadline_date)')
        .eq('template_id', copyFromId)
        .order('step_order');

      if (!fetchErr && sourceSteps?.length) {
        const { data: newSteps, error: insErr } = await supabase
          .from('process_steps')
          .insert(sourceSteps.map(s => ({
            template_id: newId,
            label:       s.label,
            step_order:  s.step_order,
          })))
          .select('id, step_order');

        if (!insErr && newSteps?.length) {
          const junctionRows = [];
          for (const ns of newSteps) {
            const src = sourceSteps.find(s => s.step_order === ns.step_order);
            for (const psd of (src?.process_step_documents ?? [])) {
              junctionRows.push({
                step_id:          ns.id,
                document_type_id: psd.document_type_id,
                item_type:        psd.item_type ?? 'document',
                deadline_date:    psd.deadline_date ?? null,
              });
            }
          }
          if (junctionRows.length > 0) {
            await supabase.from('process_step_documents').insert(junctionRows);
          }
        }
      }
    }

    await fetch();
    setSaving(false);
    return newId;
  };


  /* ── updateTemplate ─────────────────────────────────────────── */
  const updateTemplate = async (id, changes) => {
    setSaving(true);
    setError(null);
    const { error: e } = await supabase
      .from('university_process_templates')
      .update(changes)
      .eq('id', id);
    if (e) setError(e.message);
    else   await fetch();
    setSaving(false);
  };


  /* ── deleteTemplate ─────────────────────────────────────────── */
  const deleteTemplate = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    const { error: e } = await supabase
      .from('university_process_templates')
      .delete()
      .eq('id', id);
    if (e) setError(e.message);
    else   await fetch();
    setSaving(false);
  }, [fetch]); // eslint-disable-line


  /* ── setDefault ─────────────────────────────────────────────── */
  // Sets one template as default for its type, unsets all others.
  const setDefault = async (id) => {
    const template = templates.find(t => t.id === id);
    if (!template) return;
    setSaving(true);
    setError(null);

    // Unset all defaults for this type, then set the chosen one
    const { error: e1 } = await supabase
      .from('university_process_templates')
      .update({ is_default: false })
      .eq('university_id', user.universityId)
      .eq('type', template.type);

    if (e1) { setError(e1.message); setSaving(false); return; }

    const { error: e2 } = await supabase
      .from('university_process_templates')
      .update({ is_default: true })
      .eq('id', id);

    if (e2) setError(e2.message);
    else    await fetch();
    setSaving(false);
  };


  return { templates, loading, saving, error, createTemplate, updateTemplate, deleteTemplate, setDefault, refetch: fetch };
}
